import {
  DataType,
  FormDialogMode,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button } from '@material-ui/core'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumDivisa, EnumTipoCalculoConcepto } from '../../../api'
import {
  Concept,
  ConceptLayoutConfigurationModel,
  ConceptQuantityTypeOptionsModel,
  EditConceptModel,
  NewConceptPrice,
  NewConceptQuantity
} from '../../../api/Argentia/endpoints/concepts/model/concepts'
import { EnumCommodity } from '../../../api/Argentia/enums'
import { EnumClientTypes } from '../../../api/Argentia/enums/enumClientTypes'
import {
  ConceptDetailsModel,
  ConceptModel,
  LanguageModel
} from '../../../api/Argentia/model/contracts'
import ButtonWithPermissions from '../../../components/buttonWithPermissions/buttonWithPermissions'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import PageLeftColumn from '../../../components/page/pageLeftColumn'
import PageRightColumn from '../../../components/page/pageRightColumn'
import { TranslationsCard } from '../../../components/shared/translationsCard/translationsCard'
import { mapConceptToTranslationsModel } from '../../../components/shared/translationsCard/translationsCard.mapper'
import { ConceptTranslationModel } from '../../../components/shared/translationsCard/translationsCard.model'
import {
  enumEnumToValueNumber,
  enumToSelectOptionArray,
  enumTranslateOption
} from '../../../helpers'
import { cloneForTracking } from '../../../helpers/changeDetectionHelper'
import { hasPermission } from '../../../helpers/permissionsHelper'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog,
  useHttpError
} from '../../../hooks'
import { useDropdownOptions } from '../../../hooks/useDropdownOptions'
import { NonAuthRoutes } from '../../../model'
import { Resources } from '../../../model/RBAC/constants'
import { ServiceResponse } from '../../../model/serviceResponse'
import { getActiveCommodities } from '../../../redux'
import { selectActiveContract } from '../../../redux/selectors/client.selectors'
import { fetchLanguages } from '../../../services/languages.service'
import { fetchCommodityTypes } from '../../clients/client.service'
import { NewLimitDetail } from '../../flexibility/flexibility.Model'
import {
  analyzeConceptChanges,
  buildConceptPatchRequest,
  buildPrimitivePatchRequest,
  isConceptoAgregador
} from '../concepts.helpers'
import {
  enrichRelatedConceptsWithIVAType,
  mapConceptModelToConceptDetailsModel,
  mapRelatedConcepts,
  updateConceptPrimitiveFields
} from '../concepts.mapper'
import {
  fetchConceptTypesByClientType,
  fetchIVATypes,
  getBaseConceptByID,
  getConfigurationLayout,
  getQuantityTypeOptions,
  getRelatedConcepts,
  updateConcept
} from '../concepts.service'
import { ConceptDetails } from './components/conceptDetail'
import { ConceptPriceCard } from './components/conceptPriceCard'
import { ConceptQuantityCard } from './components/conceptQuantityCard'
import { RelatedConceptCard } from './components/relatedConceptCard'

export interface ConceptEditPageProps {
  location?: {
    state: {
      conceptId?: number
      clientType: EnumClientTypes
      calculationType: number
      commodity: EnumCommodity
    }
  }
}

const initialDetailTable = {
  year: new Date()
}

export const ConceptEditPage: React.FC<ConceptEditPageProps> = props => {
  const history = useHistory()
  const [t] = useTranslation(['flexibility', 'conceptsEdit', 'globalerisLibrary', 'enums'])
  const clientType = props.location?.state?.clientType ?? EnumClientTypes.FINAL_CLIENT
  const calculationType = props.location?.state?.calculationType ?? 0
  const conceptID = props.location?.state?.conceptId
  const commodity = props.location?.state?.commodity ?? EnumCommodity.GasNatural

  if (!conceptID) {
    return <></>
  }

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { info, error } = useNotify()

  const [configuration, setConfiguration] = useState<ConceptLayoutConfigurationModel>(
    {} as ConceptLayoutConfigurationModel
  )
  const [quantityType, setQuantityType] = useState<ConceptQuantityTypeOptionsModel[]>([])
  const [concept, setConcept] = useState<EditConceptModel | undefined>(undefined)
  const [initialConcept, setInitialConcept] = useState<EditConceptModel | undefined>(undefined)
  const [languages, setLanguages] = useState<LanguageModel[] | undefined>(undefined)
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [isRefreshingPrices, setIsRefreshingPrices] = useState<boolean>(false)
  const [isRefreshingRelatedConcepts, setIsRefreshingRelatedConcepts] = useState<boolean>(false)

  const [commodityOptions, setCommodityOptions] = useState<SelectOption[]>([])
  const commodities = getActiveCommodities()
  const commoditiesValues = commodities?.map(c => enumEnumToValueNumber(EnumCommodity, c))
  const commodityOptionsValuesForUser = commoditiesValues
    ? commodityOptions.filter(o => commoditiesValues.includes(o.value as number))
    : []
  const commodityOptionsForUser = commodityOptionsValuesForUser.map(option => ({
    value: option.value,
    label: enumTranslateOption(option.value as number, EnumCommodity, 'enums:EnumCommodity')
  }))

  const {
    isLoading: conceptTypesLoading,
    responseData: conceptTypesOption,
    responseError: conceptTypesError,
    fetchData: fetchDataConceptTypes
  } = useDropdownOptions<SelectOption, Parameters<typeof fetchConceptTypesByClientType>>(
    fetchConceptTypesByClientType
  )
  const {
    isLoading: ivaTypeLoading,
    responseData: ivaTypeOptions,
    responseError: ivaTypeError,
    fetchData: fetchDataIVAtypes
  } = useDropdownOptions<SelectOption, Parameters<typeof fetchIVATypes>>(fetchIVATypes)

  const contractData = useSelector(selectActiveContract)

  const {
    openForm,
    updateFormDefinition,
    closeForm,
    formProps,
    CustomFormDialog,
    setFormWaiting,
    setFormSubtitle
  } = useFormDialog()

  const [selectedDetail, setSelectedDetail] = useState<NewLimitDetail>(
    initialDetailTable as NewLimitDetail
  )

  interface formDependenciesProps {
    formState: any
  }

  const [formDependencies, setFormDependencies] = useState<formDependenciesProps>({
    formState: {}
  })

  const { showError, clearError, errorProps } = useHttpError()

  // Form Definition Starts

  const editFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'code',
                label: t('conceptsEdit:forms.edit.inputs.code'),
                type: DataType.TEXT,
                readOnly: true
              },
              {
                name: 'description',
                label: t('conceptsEdit:forms.edit.inputs.description'),
                type: DataType.TEXT
              },
              {
                name: 'conceptType',
                label: t('conceptsEdit:forms.edit.inputs.conceptType'),
                selectOptions: conceptTypesOption ?? [],
                type: DataType.SELECT,
                onChange: async (newConceptType: any) => {
                  const newIVAResponse = await fetchIVATypes(clientType, newConceptType)
                  const newIVATypeIds = new Set(
                    (newIVAResponse.data ?? []).map((o: SelectOption) => o.value as number)
                  )

                  const currentIVATypeIds = new Set(
                    (ivaTypeOptions ?? []).map(o => o.value as number)
                  )
                  const conceptHasIVA =
                    concept?.relatedConcepts?.some(rc => currentIVATypeIds.has(rc.baseConceptId)) ??
                    false

                  if (conceptHasIVA) {
                    const isCompatible =
                      concept?.relatedConcepts?.some(
                        rc =>
                          currentIVATypeIds.has(rc.baseConceptId) &&
                          newIVATypeIds.has(rc.baseConceptId)
                      ) ?? false

                    setFormSubtitle(
                      isCompatible ? '' : t('conceptsEdit:forms.edit.ivaCompatibilityWarning')
                    )
                  } else {
                    setFormSubtitle('')
                  }
                }
              },
              {
                name: 'calculationType',
                label: t('conceptsEdit:forms.edit.inputs.calculationType'),
                selectOptions: enumToSelectOptionArray(
                  EnumTipoCalculoConcepto,
                  'enums:EnumTipoCalculoConcepto'
                ),
                type: DataType.SELECT,
                readOnly: true
              },
              {
                name: 'currency',
                label: t('conceptsEdit:forms.edit.inputs.currency'),
                selectOptions: enumToSelectOptionArray(EnumDivisa, 'enums:EnumDivisa'),
                type: DataType.SELECT
              },
              {
                name: 'commodity',
                label: t('conceptsEdit:forms.edit.inputs.commodity'),
                selectOptions: commodityOptionsForUser,
                type: DataType.SELECT
              }
              // TODO si sujeto DISTRIBUCION añadir campos bypass, descbool, pxq
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      formDependencies,
      ivaTypeLoading,
      conceptTypesLoading,
      ivaTypeOptions,
      conceptTypesOption,
      commodityOptionsForUser,
      concept?.relatedConcepts
    ])

  // Form Config Ends

  // Handlers Starts

  const onEditConceptDetailsClickHandler = async () => {
    setFormSubtitle('')
    const conceptData = {
      ...concept
    } as ConceptModel

    openForm({
      definition: editFormDefinition,
      title: t('conceptsEdit:forms.edit.title'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: conceptData,
      onClose: onEditConceptDetailsHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: conceptData
    })
  }

  const onEditConceptDetailsHandler = async (formData: ConceptDetailsModel) => {
    if (formData) {
      // Para la edición de cabecera (modal), solo enviar propiedades primitivas
      // No incluir translations/quantities/prices que no se editaron en este formulario
      const patchRequest = buildPrimitivePatchRequest(concept!.id, formData)

      setFormWaiting(true)
      await updateConcept(patchRequest)
        .then(response => {
          if (response.success) {
            info(t('conceptsEdit:notifications.save.success'))

            // Actualizar solo propiedades primitivas sin refetch completo
            // para no perder cambios pendientes de translations/quantities/prices
            setConcept(prevConcept => {
              if (!prevConcept) return prevConcept
              return updateConceptPrimitiveFields(prevConcept, formData)
            })

            // También actualizar initialConcept para mantener change detection sincronizado
            setInitialConcept(prevInitial => {
              if (!prevInitial) return prevInitial
              return updateConceptPrimitiveFields(prevInitial, formData)
            })
          } else {
            error(t('conceptsEdit:notifications.save.error'))
          }
        })
        .finally(() => {
          setFormWaiting(false)
        })
    }

    closeForm()
  }

  // Handlers Ends

  // Functions Starts

  const getCommodityOptions = async () => {
    await fetchCommodityTypes().then(response => {
      setCommodityOptions(response.data)
    })
  }

  const fetchconceptsEditByID = async (id: number) => {
    setIsFetching(true)
    let fetchingArray: Promise<any>[] = [getBaseConceptByID(id)]
    if (!isConceptoAgregador(calculationType)) {
      fetchingArray.push(getRelatedConcepts(id))
    }
    await Promise.all(fetchingArray)
      .then(responses => {
        const baseConceptResponse = responses[0] as ServiceResponse<Concept>
        if (baseConceptResponse.success && baseConceptResponse.data) {
          clearError()
          const conceptData = baseConceptResponse.data

          if (responses.length > 1) {
            const relatedConceptsResponse = responses[1] as ServiceResponse<Concept[]>
            if (relatedConceptsResponse.success && relatedConceptsResponse.data) {
              conceptData.relatedConcepts = mapRelatedConcepts(
                relatedConceptsResponse.data,
                conceptID
              )
            } else {
              error(t('conceptsEdit:notifications.fetchRelatedConcepts.error'))
            }
          }

          setConcept(conceptData)
          // Guardar copia del estado inicial para detectar cambios
          setInitialConcept(cloneForTracking(conceptData))
        } else {
          baseConceptResponse.error &&
            showError({
              title: t('conceptsEdit:title'),
              errorCode: baseConceptResponse.error.code,
              onRefresh: async () => {
                fetchconceptsEditByID(id)
              }
            })
        }
      })
      .finally(() => setIsFetching(false))
  }

  const handleGoBack = () => {
    goBack()
  }

  const goBack = () => {
    if (contractData) {
      history.push(NonAuthRoutes.ContractFlexibilities)
    } else {
      history.goBack()
    }
  }

  const onSave = async (newConcept: any, redirect?) => {
    if (!concept?.id || !initialConcept) {
      return
    }

    // Analizar qué cambió en el concepto
    const changeAnalysis = analyzeConceptChanges(initialConcept, concept)

    // Verificar si hay cambios antes de guardar
    if (!changeAnalysis.hasChanges) {
      info(t('conceptsEdit:notifications.noChanges'))
      return
    }

    // Construir request optimizado con solo lo que cambió
    const patchRequest = buildConceptPatchRequest(initialConcept, concept)

    // Enviar al backend
    await updateConcept(patchRequest).then(response => {
      if (response.success) {
        info(t('conceptsEdit:notifications.save.success'))
        if (redirect) {
          redirect()
        } else {
          fetchconceptsEditByID(conceptID) // Recargar concepto actualizado
        }
      } else {
        error(t('conceptsEdit:notifications.save.error'))
      }
    })
  }

  const handleAddTranslationsRow = (formData: ConceptTranslationModel) => {
    const newProductDetail: EditConceptModel = { ...concept! }

    const newTranslation = {
      description: formData.description,
      language: formData.language,
      tempId: formData.tempId
    } as ConceptTranslationModel

    newProductDetail.translations = [newTranslation, ...newProductDetail?.translations!]
    setConcept(newProductDetail)
  }

  const handleEditTranslationsRow = (rowData: ConceptTranslationModel) => {
    const newConcept: EditConceptModel = { ...concept! }

    let property: number | string

    if (rowData?.id !== undefined) property = 'id'
    else property = 'tempId'

    let newTranslation = newConcept?.translations?.find(e => e[property] === rowData[property])

    if (newTranslation) {
      newTranslation.description = rowData.description
      newTranslation.language = rowData.language
      newTranslation.tempId = rowData.tempId ? rowData.tempId : newTranslation.tempId
    }

    setConcept(newConcept)
  }

  const handleDeleteTranslationsRow = (rowData: ConceptTranslationModel) => {
    let property: string

    if (rowData?.id !== undefined) property = 'id'
    else property = 'tempId'

    const newTranslation = concept?.translations?.filter(e => e[property] !== rowData[property])

    setConcept(prevState => {
      if (prevState) {
        return {
          ...prevState,
          translations: newTranslation
        } as EditConceptModel
      }
      return prevState
    })
  }
  // Functions Ends

  // Hooks Starts

  const fetchLanguagesSetup = async () => {
    await fetchLanguages().then(response => {
      if (response.success) {
        setLanguages(response.data)
      } else {
        error(t('products:notifications.FetchLanguages.error'))
        return []
      }
    })
  }

  const fetchLayoutConfiguration = async (calculationType: number) => {
    // setIsFetching(true)
    await getConfigurationLayout(calculationType)
      .then(response => {
        if (response.success) {
          const config = response.data as ConceptLayoutConfigurationModel
          setConfiguration(config)
        } else {
          error(t('conceptsEdit:notifications.FetchLayoutConfiguration.error'))
        }
      })
      .finally(() => {
        // setIsFetching(false)
      })
  }

  const handleQuantityChange = (quantity: NewConceptQuantity) => {
    const quantityIndex = concept?.conceptQuantities?.find(
      q => q.id === quantity.id || q.tempId === quantity.tempId
    )

    const updatedQuantities = quantityIndex
      ? concept?.conceptQuantities?.map(q => {
          if (q.id === quantity.id || q.tempId === quantity.tempId) {
            return { ...q, ...quantity }
          }
          return q
        })
      : [...concept?.conceptQuantities!, quantity]

    setConcept((prevState: any) => {
      return {
        ...prevState,
        conceptQuantities: updatedQuantities
      }
    })
  }

  const handleAddConceptPrice = (price: NewConceptPrice) => {
    // Implementado en ConceptPriceCard para simplificar
  }

  const handleEditConceptPrice = (price: NewConceptPrice) => {
    // Implementado en ConceptPriceCard para simplificar
  }

  const handleRefreshConceptAfterPriceEdit = async () => {
    // Actualizar solo conceptPrice sin refetch completo para no perder cambios pendientes
    // en translations/quantities
    setIsRefreshingPrices(true)
    const response = await getBaseConceptByID(conceptID)

    if (response.success && response.data) {
      // Actualizar solo conceptPrice en el concepto actual
      setConcept(prevConcept => {
        if (!prevConcept) return prevConcept
        return {
          ...prevConcept,
          conceptPrice: response.data!.conceptPrice // Solo actualizar precios
        }
      })

      // También actualizar initialConcept.conceptPrice para mantener
      // el change detection sincronizado con los nuevos precios
      // finalmente igual no se está utilizando por ahora esta comparación puesto que dispara fetch directo la edición de conceptPrice, pero lo dejo preparado para cuando se implemente la edición de precios desde esta pantalla
      setInitialConcept(prevInitial => {
        if (!prevInitial) return prevInitial
        return {
          ...prevInitial,
          conceptPrice: cloneForTracking(response.data!.conceptPrice)
        }
      })
    } else {
      error(t('conceptsEdit:notifications.refreshPrice.error'))
    }
    setIsRefreshingPrices(false)
  }

  const handleRefreshConceptAfterRelatedConceptAdded = async () => {
    // Actualizar solo relatedConcepts sin refetch completo
    setIsRefreshingRelatedConcepts(true)

    if (isConceptoAgregador(concept?.calculationType || calculationType)) {
      // Para conceptos agregadores, getBaseConceptByID trae relatedConcepts incluidos
      const response = await getBaseConceptByID(conceptID)

      if (response.success && response.data) {
        setConcept(prevConcept => {
          if (!prevConcept) return prevConcept
          return {
            ...prevConcept,
            relatedConcepts: response.data!.relatedConcepts
          }
        })

        setInitialConcept(prevInitial => {
          if (!prevInitial) return prevInitial
          return {
            ...prevInitial,
            relatedConcepts: cloneForTracking(response.data!.relatedConcepts)
          }
        })
      } else {
        error(t('conceptsEdit:notifications.refreshRelatedConcepts.error'))
      }
    } else {
      // Para conceptos no agregadores, necesitamos llamada separada a getRelatedConcepts
      const response = await getRelatedConcepts(conceptID)

      if (response.success && response.data) {
        const mappedRelatedConcepts = mapRelatedConcepts(response.data, conceptID)

        setConcept(prevConcept => {
          if (!prevConcept) return prevConcept
          return {
            ...prevConcept,
            relatedConcepts: mappedRelatedConcepts
          }
        })

        setInitialConcept(prevInitial => {
          if (!prevInitial) return prevInitial
          return {
            ...prevInitial,
            relatedConcepts: cloneForTracking(mappedRelatedConcepts)
          }
        })
      } else {
        error(t('conceptsEdit:notifications.refreshRelatedConcepts.error'))
      }
    }

    setIsRefreshingRelatedConcepts(false)
  }

  useEffect(() => {
    fetchLayoutConfiguration(calculationType)
    getCommodityOptions()
    fetchLanguagesSetup()
    fetchconceptsEditByID(conceptID)
  }, [])

  useEffect(() => {
    // Wait for conceptType to be loaded to avoid fetchDataConceptTypes triggering twice
    if (concept?.conceptType) {
      fetchDataConceptTypes(clientType)
      concept?.conceptType && fetchDataIVAtypes(clientType, concept.conceptType)
    }
  }, [clientType, concept?.conceptType])

  useEffect(() => {
    const calculationTypeToFetch = concept?.calculationType || calculationType
    if (calculationTypeToFetch) {
      getQuantityTypeOptions(calculationTypeToFetch).then(response => {
        if (response.success) {
          // Asegurar que siempre sea un array
          setQuantityType(Array.isArray(response.data) ? response.data : [])
        } else {
          error(t('conceptsEdit:notifications.FetchQuantityTypeOptions.error'))
        }
      })
    }
  }, [concept?.calculationType, calculationType])

  // Hooks Ends

  // Detectar si hay cambios para habilitar/deshabilitar el botón Guardar
  const hasChanges = useMemo(() => {
    if (!concept || !initialConcept) return false
    const changeAnalysis = analyzeConceptChanges(initialConcept, concept)
    return changeAnalysis.hasChanges
  }, [concept, initialConcept])

  const conceptTranslations = concept ? mapConceptToTranslationsModel(concept) : null
  const conceptDetails =
    concept && ivaTypeOptions && conceptTypesOption && !ivaTypeLoading && !conceptTypesLoading
      ? mapConceptModelToConceptDetailsModel(concept, {
          IVAtypeOptions: ivaTypeOptions,
          conceptTypesOption
        })
      : null
  const quantities = conceptDetails?.conceptQuantities ? conceptDetails?.conceptQuantities[0] : null

  // Enriquecer relatedConcepts con canChangeIVAType cuando ambos datos estén disponibles
  const enrichedRelatedConcepts = useMemo(() => {
    if (!concept?.relatedConcepts || !ivaTypeOptions) return concept?.relatedConcepts
    return enrichRelatedConceptsWithIVAType(concept.relatedConcepts, ivaTypeOptions)
  }, [concept?.relatedConcepts, ivaTypeOptions])

  return (
    <Page layout={PageLayout.MULTI_COLUMN} error={errorProps}>
      <PageLeftColumn>
        <ConceptDetails conceptDetails={conceptDetails} />
      </PageLeftColumn>
      <PageCenterColumn>
        <PageHeader title={t('conceptsEdit:title')}>
          <Button onClick={handleGoBack} size="small" color="primary">
            {t('conceptsEdit:mainActions.back')}
          </Button>
          <ButtonWithPermissions
            onClick={onEditConceptDetailsClickHandler}
            size="small"
            color="primary"
            hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          >
            {t('conceptsEdit:mainActions.edit')}
          </ButtonWithPermissions>
          <ButtonWithPermissions
            variant="contained"
            disabled={!hasChanges}
            onClick={() =>
              onSave(concept, () => {
                history.push(NonAuthRoutes.Concepts, {
                  clientType: clientType
                })
              })
            }
            size="small"
            color="primary"
            hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
          >
            {t('conceptsEdit:mainActions.save')}
          </ButtonWithPermissions>
        </PageHeader>

        {(configuration?.layout === 2 || configuration?.layout === 3) && (
          <ConceptQuantityCard
            quantityType={quantityType}
            conceptQuantity={quantities}
            onChangeInput={handleQuantityChange}
          />
        )}

        {(configuration.layout === 3 || configuration.layout === 4) && (
          <ConceptPriceCard
            isFetching={isFetching || isRefreshingPrices}
            conceptPrice={concept?.conceptPrice}
            conceptId={conceptID}
            onAddConceptPrice={handleAddConceptPrice}
            onEditPrice={handleEditConceptPrice}
            onRefreshPage={handleRefreshConceptAfterPriceEdit}
            configuration={configuration}
            commodity={commodity}
          />
        )}

        <RelatedConceptCard
          conceptId={conceptID}
          calculationType={concept?.calculationType || calculationType}
          relatedConcepts={enrichedRelatedConcepts}
          isFetching={isFetching || isRefreshingRelatedConcepts}
          clientType={clientType}
          commodity={commodity}
          ivaTypeOptions={ivaTypeOptions ?? []}
          onRefresh={handleRefreshConceptAfterRelatedConceptAdded}
          canAddRelatedConcepts
        />

        <TranslationsCard
          translations={conceptTranslations}
          languages={languages ?? []}
          onAddTranslationsRowValues={formData => handleAddTranslationsRow(formData)}
          onEditTranslationsRowValues={rowData => handleEditTranslationsRow(rowData)}
          onDeleteTranslationsRowValues={rowData => handleDeleteTranslationsRow(rowData)}
          isFetching={!conceptTranslations}
          // isFetching={isFetchingConceptTranslations}
        />

        {/* {(configuration?.layout === 2 || configuration?.layout === 3) && <div>Card Cantidad</div>}
        {configuration?.layout === 3 && <div>Card Price</div>}  */}

        {/* <FlexibilityPrice
          price={concept?.price}
          onChangeInput={handleSelect}
          onIsValid={handlePriceIsValid}
        /> */}

        <CustomFormDialog {...formProps}></CustomFormDialog>
      </PageCenterColumn>
      <PageRightColumn></PageRightColumn>
    </Page>
  )
}
